import React from "react";
import axios from "axios";
import moment from "moment";
import { parseServerDate } from "../../Utils/serverDate";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Popper from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import FolderIcon from "@material-ui/icons/Folder";
import InboxIcon from "@material-ui/icons/MoveToInbox";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AddIcon from "@material-ui/icons/Add";
import CreateIcon from "@material-ui/icons/Create";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  ConversationHeader,
  Sidebar,
  Search,
  ConversationList,
  Conversation,
} from "@chatscope/chat-ui-kit-react";

import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

import Header from "./../../assets/components/header";
import Constants from "./../../Utils/Constants";
import { getHeaders, getUser } from "./../../Utils/Common";
import store from "./../../Common/storage";
import toastMaker from "./../../Common/toastmaker";

import "./index.css";

const DURATIONS = [
  { label: "1 Hour",   getStartTs: () => moment().subtract(1, "hours").valueOf(),  getEndTs: () => moment().valueOf() },
  { label: "3 Hours",  getStartTs: () => moment().subtract(3, "hours").valueOf(),  getEndTs: () => moment().valueOf() },
  { label: "1 Day",    getStartTs: () => moment().subtract(1, "days").valueOf(),   getEndTs: () => moment().valueOf() },
  { label: "1 Week",   getStartTs: () => moment().subtract(1, "weeks").valueOf(),  getEndTs: () => moment().valueOf() },
  { label: "1 Month",  getStartTs: () => moment().subtract(1, "months").valueOf(), getEndTs: () => moment().valueOf() },
  { label: "3 Months", getStartTs: () => moment().subtract(3, "months").valueOf(), getEndTs: () => moment().valueOf() },
  { label: "6 Months", getStartTs: () => moment().subtract(6, "months").valueOf(), getEndTs: () => moment().valueOf() },
  { label: "1 Year",   getStartTs: () => moment().subtract(1, "years").valueOf(),  getEndTs: () => moment().valueOf() },
];

const useStyles = makeStyles({ root: { width: "100%" } });

function useDebounce(fn, delay) {
  const fnRef = React.useRef(fn);
  React.useLayoutEffect(() => { fnRef.current = fn; });
  const timerRef = React.useRef(null);
  return React.useCallback(
    (...args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay] // eslint-disable-line react-hooks/exhaustive-deps
  );
}

const getFolderIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n === "inbox")  return <InboxIcon style={{ fontSize: "15px" }} />;
  if (n === "closed/stopped") return <CheckCircleOutlineIcon style={{ fontSize: "15px" }} />;
  return <FolderIcon style={{ fontSize: "15px" }} />;
};

export default function ConversationsV2() {
  // ── Refs ───────────────────────────────────────────────────────────
  const anchorRef          = React.useRef(null);
  const activeNumberRef    = React.useRef(null);
  const lastThreadMsgAt    = React.useRef(null);
  const prevOpen           = React.useRef(false);
  const tagFilterAnchorRef = React.useRef(null);

  // ── Core state ─────────────────────────────────────────────────────
  const [contacts,        setContacts]        = React.useState([]);
  const [activeNumber,    setActiveNumber]    = React.useState(null);
  const [thread,          setThread]          = React.useState([]);
  const [threadLoading,   setThreadLoading]   = React.useState(false);
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [contactData,     setContactData]     = React.useState({});
  const [contactNotFound, setContactNotFound] = React.useState(false);
  const [contactLoading,  setContactLoading]  = React.useState(false);
  const [msgInputValue,   setMsgInputValue]   = React.useState("");

  // ── Filters ────────────────────────────────────────────────────────
  const [activeFolder,  setActiveFolder]  = React.useState(null);
  const [searchTerm,    setSearchTerm]    = React.useState("");
  const [duration,      setDuration]      = React.useState(DURATIONS[3]);
  const [dateRange,     setDateRange]     = React.useState(null);
  const [onlyUnread,    setOnlyUnread]    = React.useState(false);
  const [onlyReplies,   setOnlyReplies]   = React.useState(false);

  // ── Virtual number ─────────────────────────────────────────────────
  const [virtualNumberId, setVirtualNumberId] = React.useState(null);
  const [longcodes,       setLongcodes]       = React.useState([]);

  // ── Folder management ──────────────────────────────────────────────
  const [folders,            setFolders]            = React.useState([]);
  const [isAddingFolder,     setIsAddingFolder]     = React.useState(false);
  const [newFolderName,      setNewFolderName]      = React.useState("");
  const [editingFolderId,    setEditingFolderId]    = React.useState(null);
  const [editingFolderName,  setEditingFolderName]  = React.useState("");
  const [convMoveAnchor,     setConvMoveAnchor]     = React.useState(null);
  const [convMoveTarget,     setConvMoveTarget]     = React.useState(null);
  const [convMoveCurrentFld, setConvMoveCurrentFld] = React.useState(null);

  // ── Contact tags ──────────────────────────────────────────────────────
  const [contactTags,      setContactTags]      = React.useState([]);
  const [tagSearch,        setTagSearch]        = React.useState("");
  const [tagSearchResults, setTagSearchResults] = React.useState([]);
  const [addingTagId,      setAddingTagId]      = React.useState(null);

  // ── Tag filter ────────────────────────────────────────────────────────
  const [activeTagFilter,  setActiveTagFilter]  = React.useState(null);
  const [tagFilterSearch,  setTagFilterSearch]  = React.useState("");
  const [tagFilterResults, setTagFilterResults] = React.useState([]);
  const [tagFilterOpen,    setTagFilterOpen]    = React.useState(false);

  // ── Header 3-dot ───────────────────────────────────────────────────
  const [open, setOpen] = React.useState(false);

  // ── User/config ────────────────────────────────────────────────────
  const user = getUser();
  const isTwoWaySMSEnabled =
    user?.configs?.[store.get("c_id_index")]?.longcodes?.length > 0;

  // ── Sync refs ──────────────────────────────────────────────────────
  React.useEffect(() => { activeNumberRef.current = activeNumber; }, [activeNumber]);

  React.useEffect(() => {
    if (prevOpen.current === true && open === false) anchorRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  // ── Init virtual number ────────────────────────────────────────────
  React.useEffect(() => {
    if (!isTwoWaySMSEnabled) return;
    const lcs = user.configs[store.get("c_id_index")].longcodes;
    setLongcodes(lcs);
    setVirtualNumberId(lcs[0]?.number);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load folders ───────────────────────────────────────────────────
  React.useEffect(() => {
    axios
      .get(
        Constants.getApiPathWithConfigId(Constants.virtualNumberFoldersApiPath),
        getHeaders()
      )
      .then((res) => {
        const fetched = (res.data.data || []).map((f) => ({
          id: f.id,
          name: f.name,
          isSystem: f.is_system || false,
        }));
        setFolders(fetched);
        if (fetched.length > 0) setActiveFolder(fetched[0].id);
      })
      .catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Contacts polling ───────────────────────────────────────────────
  React.useEffect(() => {
    if (!virtualNumberId) return;
    let keepRunning  = true;
    let isFirstLoad  = true;

    const poll = () => {
      if (!keepRunning) return;
      const params = {
        startTs: duration.getStartTs(),
        endTs:   duration.getEndTs(),
      };
      if (activeTagFilter) params.tag_id = activeTagFilter.id;
      if (isFirstLoad) setContactsLoading(true);

      const url = Constants.getApiPathWithConfigId(Constants.v2ContactsApiPath)
        .replace("{v_number}", virtualNumberId);

      axios
        .get(url, { ...getHeaders(), params })
        .then((res) => {
          if (!keepRunning) return;
          const newContacts = (res.data.data || []).sort(
            (a, b) =>
              new Date(b.last_message_at).getTime() -
              new Date(a.last_message_at).getTime()
          );
          setContacts(newContacts);

          // Auto-refresh open thread when the active contact has a new message
          const num = activeNumberRef.current;
          if (num && !isFirstLoad) {
            const updated = newContacts.find((c) => c.number === num);
            if (updated && updated.last_message_at !== lastThreadMsgAt.current) {
              lastThreadMsgAt.current = updated.last_message_at;
              fetchThread(num, virtualNumberId, true);
            }
          }

          if (isFirstLoad) { setContactsLoading(false); isFirstLoad = false; }
          if (keepRunning) setTimeout(poll, 5000);
        })
        .catch((err) => {
          console.error(err);
          if (isFirstLoad) { setContactsLoading(false); isFirstLoad = false; }
          if (keepRunning) setTimeout(poll, 5000);
        });
    };

    poll();
    return () => { keepRunning = false; };
  }, [duration, virtualNumberId, activeTagFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch thread ───────────────────────────────────────────────────
  const fetchThread = (number, vNum, silent = false) => {
    const vNumber = vNum || virtualNumberId;
    if (!number || !vNumber) return;
    if (!silent) {
      setThread([]);
      setThreadLoading(true);
    }

    const url = Constants.getApiPathWithConfigId(Constants.v2ThreadApiPath)
      .replace("{v_number}", vNumber)
      .replace("{number}", number);

    axios
      .get(url, { ...getHeaders(), params: { limit: 500 } })
      .then((res) => {
        setThread(res.data.data || []);
        setThreadLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setThreadLoading(false);
        toastMaker.makeToast("Failed to load messages.", { variant: "error" });
      });
  };

  // ── Open conversation ──────────────────────────────────────────────
  const openConversation = (contact) => {
    setActiveNumber(contact.number);
    activeNumberRef.current  = contact.number;
    lastThreadMsgAt.current  = contact.last_message_at;
    setContactData({});
    setContactNotFound(false);
    setContactLoading(true);
    setContactTags([]);
    setTagSearch("");
    setTagSearchResults([]);
    fetchContactTags(contact.number);

    if (contact.unread_count > 0) {
      setContacts((prev) =>
        prev.map((c) =>
          c.number === contact.number ? { ...c, unread_count: 0 } : c
        )
      );
      markRead(contact.number);
    }

    fetchThread(contact.number);

    const handleContactResponse = (r) => {
      setContactLoading(false);
      const data = r.data.data;
      if (!data) {
        setContactNotFound(true);
        toastMaker.makeToast("Contact not found in ActiveCampaign - messaging disabled.", { variant: "warning" });
      } else {
        setContactData(data);
      }
    };

    const handleContactError = (err) => {
      setContactLoading(false);
      const msg = err?.response?.data?.message || "";
      if (msg.toLowerCase().includes("not found") || err?.response?.status === 404) {
        setContactNotFound(true);
        toastMaker.makeToast("Contact not found in ActiveCampaign - messaging disabled.", { variant: "warning" });
      } else {
        console.error(err);
      }
    };

    const acId = contact.ac_id;
    if (acId && acId !== "0" && acId !== 0) {
      axios
        .get(
          Constants.getApiPathWithConfigId(Constants.getACContactDetailsById)
            .replace("{ac_id}", acId),
          getHeaders()
        )
        .then(handleContactResponse)
        .catch(handleContactError);
    } else {
      axios
        .get(
          Constants.getApiPathWithConfigId(Constants.getACContactDetailsByMobile)
            .replace("{number}", contact.number),
          getHeaders()
        )
        .then(handleContactResponse)
        .catch(handleContactError);
    }

    // Mobile: show chat panel
    const chatEl = document.getElementById("v2-chatcontainer");
    const sideEl = document.getElementById("v2-sidebar");
    if (chatEl) chatEl.style = null;
    if (sideEl) sideEl.style = null;
  };

  // ── Mark read ──────────────────────────────────────────────────────
  const markRead = (number) => {
    if (!virtualNumberId) return;
    const url = Constants.getApiPathWithConfigId(Constants.v2MarkReadApiPath)
      .replace("{v_number}", virtualNumberId)
      .replace("{number}", number);
    axios.put(url, {}, getHeaders()).catch(console.error);
  };

  // ── Mark unread ────────────────────────────────────────────────────
  const markUnread = (number) => {
    if (!virtualNumberId) return;
    setContacts((prev) =>
      prev.map((c) => (c.number === number ? { ...c, unread_count: c.unread_count > 0 ? c.unread_count : 1 } : c))
    );
    const url = Constants.getApiPathWithConfigId(Constants.v2MarkUnreadApiPath)
      .replace("{v_number}", virtualNumberId)
      .replace("{number}", number);
    axios.put(url, {}, getHeaders()).catch((err) => {
      console.error(err);
      toastMaker.makeToast("Failed to mark as unread.", { variant: "error" });
    });
  };

  const toggleReadStatus = (number, isCurrentlyUnread) => {
    if (isCurrentlyUnread) {
      setContacts((prev) =>
        prev.map((c) => (c.number === number ? { ...c, unread_count: 0 } : c))
      );
      markRead(number);
    } else {
      markUnread(number);
    }
  };

  // ── Mark all read ──────────────────────────────────────────────────
  const markAllRead = () => {
    if (!virtualNumberId) return;
    setContacts((prev) => prev.map((c) => ({ ...c, unread_count: 0 })));
    const url = Constants.getApiPathWithConfigId(Constants.v2MarkAllReadApiPath)
      .replace("{v_number}", virtualNumberId);
    axios.put(url, {}, getHeaders()).catch((err) => {
      console.error(err);
      toastMaker.makeToast("Failed to mark all as read.", { variant: "error" });
    });
  };

  // ── Send reply ─────────────────────────────────────────────────────
  const sendReply = (innerHtml, textContent, innerText) => {
    if (!activeNumber || !virtualNumberId) return;
    const message = innerText;
    const tempId  = `opt_${Date.now()}`;

    setThread((prev) => [
      ...prev,
      { id: tempId, type: "outgoing", message, created_at: new Date().toISOString(), isOptimistic: true },
    ]);
    setMsgInputValue("");

    axios
      .post(
        Constants.getApiPathWithConfigId(Constants.sendtwowaysmsreplyApiPath),
        { number: activeNumber, ac_id: contactData?.id, virtual_number: virtualNumberId, message },
        getHeaders()
      )
      .then(() => fetchThread(activeNumber, null, true))
      .catch((err) => {
        console.error(err);
        setThread((prev) => prev.filter((m) => m.id !== tempId));
        toastMaker.makeToast("Failed to send message.", { variant: "error" });
      });
  };

  // ── Tag helpers ────────────────────────────────────────────────────
  const searchTags = (query, setter) => {
    const url = Constants.getApiPathWithConfigId(Constants.v2TagsApiPath);
    axios
      .get(url, { ...getHeaders(), params: { search: query } })
      .then((res) => setter(res.data.data || []))
      .catch(console.error);
  };

  const debouncedTagSearch       = useDebounce((q) => searchTags(q, setTagSearchResults),  300);
  const debouncedTagFilterSearch = useDebounce((q) => searchTags(q, setTagFilterResults), 300);

  const fetchContactTags = (number, vNum) => {
    const vNumber = vNum || virtualNumberId;
    if (!number || !vNumber) return;
    const url = Constants.getApiPathWithConfigId(Constants.v2ContactTagsApiPath)
      .replace("{v_number}", vNumber)
      .replace("{number}", number);
    axios
      .get(url, getHeaders())
      .then((res) => setContactTags(res.data.data || []))
      .catch(console.error);
  };

  const addContactTag = (tag) => {
    setAddingTagId(tag.id);
    const url = Constants.getApiPathWithConfigId(Constants.v2ContactTagsBaseApiPath);
    axios
      .post(url, {
        number:         activeNumber,
        virtual_number: virtualNumberId,
        tag_id:         tag.id,
        tag_name:       tag.name,
      }, getHeaders())
      .then((res) => {
        setAddingTagId(null);
        if (res.status === 200) {
          toastMaker.makeToast("Tag already applied", { variant: "info" });
        } else {
          fetchContactTags(activeNumber);
        }
        setTagSearch("");
        setTagSearchResults([]);
      })
      .catch((err) => {
        setAddingTagId(null);
        const status = err?.response?.status;
        const msg    = err?.response?.data?.message;
        if (status === 404) {
          toastMaker.makeToast("Contact not found", { variant: "error" });
        } else {
          toastMaker.makeToast(msg || "Failed to add tag", { variant: "error" });
        }
      });
  };

  const removeContactTag = (id) => {
    const url =
      Constants.getApiPathWithConfigId(Constants.v2ContactTagsBaseApiPath) + `/${id}`;
    axios
      .delete(url, getHeaders())
      .then(() => setContactTags((prev) => prev.filter((t) => t.id !== id)))
      .catch((err) => {
        console.error(err);
        toastMaker.makeToast("Failed to remove tag", { variant: "error" });
      });
  };

  // ── Folder CRUD ────────────────────────────────────────────────────
  const addFolder = () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    if (folders.filter((f) => !f.isSystem).length >= Constants.maxCustomFolders) return;
    if (folders.some((f) => f.name.toLowerCase() === trimmed.toLowerCase())) {
      alert(`A folder named "${trimmed}" already exists.`);
      return;
    }
    axios
      .post(
        Constants.getApiPathWithConfigId(Constants.virtualNumberFoldersApiPath),
        { name: trimmed },
        getHeaders()
      )
      .then((res) => {
        const nf = res.data.data;
        setFolders((prev) => {
          const updated = [...prev];
          let insertAt = updated.length;
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].isSystem) insertAt = i;
            else break;
          }
          updated.splice(insertAt, 0, { id: nf.id, name: nf.name, isSystem: false });
          return updated;
        });
        setNewFolderName("");
        setIsAddingFolder(false);
      })
      .catch((err) => {
        console.error(err);
        toastMaker.makeToast("Failed to create folder.", { variant: "error" });
      });
  };

  const renameFolder = (folderId) => {
    const trimmed = editingFolderName.trim();
    if (!trimmed) return;
    if (
      folders.some(
        (f) => f.id !== folderId && f.name.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      alert(`A folder named "${trimmed}" already exists.`);
      return;
    }
    axios
      .put(
        Constants.getApiPathWithConfigId(Constants.virtualNumberFoldersApiPath) + `/${folderId}`,
        { name: trimmed },
        getHeaders()
      )
      .then(() => {
        setFolders((prev) =>
          prev.map((f) => (f.id === folderId ? { ...f, name: trimmed } : f))
        );
        setEditingFolderId(null);
        setEditingFolderName("");
      })
      .catch((err) => {
        console.error(err);
        toastMaker.makeToast("Failed to rename folder.", { variant: "error" });
      });
  };

  const deleteFolder = (folderId) => {
    axios
      .delete(
        Constants.getApiPathWithConfigId(Constants.virtualNumberFoldersApiPath) + `/${folderId}`,
        getHeaders()
      )
      .then(() => {
        setFolders((prev) => prev.filter((f) => f.id !== folderId));
        if (activeFolder === folderId) {
          setActiveFolder(folders.find((f) => f.id !== folderId)?.id ?? null);
        }
      })
      .catch((err) => {
        console.error(err);
        toastMaker.makeToast("Failed to delete folder.", { variant: "error" });
      });
  };

  const moveConversationToFolder = (number, folderId) => {
    setContacts((prev) =>
      prev.map((c) => (c.number === number ? { ...c, folder_id: folderId } : c))
    );
    setConvMoveAnchor(null);
    setConvMoveTarget(null);
    setConvMoveCurrentFld(null);

    axios
      .post(
        Constants.getApiPathWithConfigId(Constants.v2MoveFolderApiPath),
        { number, virtual_number: virtualNumberId, folder_id: folderId },
        getHeaders()
      )
      .catch((err) => {
        console.error(err);
        toastMaker.makeToast("Failed to move conversation.", { variant: "error" });
      });
  };

  // ── 3-dot header menu ──────────────────────────────────────────────
  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose  = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };
  const handleListKeyDown = (event) => {
    if (event.key === "Tab")    { event.preventDefault(); setOpen(false); }
    if (event.key === "Escape") { setOpen(false); }
  };

  // ── Derived state ──────────────────────────────────────────────────
  const filteredContacts = contacts
    .filter((c) => activeFolder === null || c.folder_id === activeFolder)
    .filter(
      (c) =>
        !searchTerm ||
        c.number.includes(searchTerm) ||
        (c.name || "").toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((c) => !onlyUnread  || c.unread_count > 0)
    .filter((c) => !onlyReplies || c.has_reply === true);

  const selectedContact      = contacts.find((c) => c.number === activeNumber) || null;
  const convMoveTargetContact = contacts.find((c) => c.number === convMoveTarget) || null;

  const folderCounts = {};
  folders.forEach((f) => { folderCounts[f.id] = { total: 0, unread: 0 }; });
  contacts.forEach((c) => {
    if (!folderCounts[c.folder_id]) folderCounts[c.folder_id] = { total: 0, unread: 0 };
    folderCounts[c.folder_id].total++;
    if (c.unread_count > 0) folderCounts[c.folder_id].unread++;
  });

  const hasUnread = contacts.some((c) => c.unread_count > 0);
  const classes   = useStyles();

  // ── Guard ──────────────────────────────────────────────────────────
  if (!isTwoWaySMSEnabled) {
    return (
      <div style={{ padding: "40px", color: "#6B6C7E" }}>
        Conversations is not enabled. Please contact support.
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div>
      <Header
        name={`Conversations${filteredContacts.length ? ` (${filteredContacts.length})` : ""}`}
      />

      {/* ── Main panel ── */}
      <div className="conversation-section">
        <Paper className={classes.root}>

          {/* ── Compact top bar: duration filter + mark-all-read ── */}
          <div className="v2-topbar">
            <span className="v2-topbar__label">Past:</span>
            {DURATIONS.map((d) => (
              <span
                key={d.label}
                onClick={() => { setDuration(d); setDateRange(null); }}
                className={duration === d ? "convo-selections-4765--selected" : "convo-selections-4765"}
                style={{ marginLeft: "6px", marginRight: 0 }}
              >
                {d.label}
              </span>
            ))}
            <span style={{ marginLeft: "10px" }}>
              <DateRangePicker
                onChange={(dates) => {
                  if (!dates) {
                    setDuration(DURATIONS[3]);
                  } else {
                    setDuration({
                      getStartTs: () => moment(dates[0]).valueOf(),
                      getEndTs:   () => moment(dates[1]).valueOf(),
                    });
                  }
                  setDateRange(dates);
                }}
                value={dateRange}
                calendarIcon={<CalendarTodayIcon style={{ width: 16, height: 16, color: "#122643" }} />}
              />
            </span>
            <span className="v2-topbar__toggle">
              <Switch
                size="small"
                checked={onlyUnread}
                onChange={(e) => setOnlyUnread(e.target.checked)}
                color="primary"
              />
              <span className="v2-topbar__toggle-label">Unread only</span>
            </span>
            <span className="v2-topbar__toggle">
              <Switch
                size="small"
                checked={onlyReplies}
                onChange={(e) => setOnlyReplies(e.target.checked)}
                color="primary"
              />
              <span className="v2-topbar__toggle-label">Replies only</span>
            </span>
            <div style={{ flex: 1 }} />
            {hasUnread && (
              <span
                className="c-button c-button--secondary text-uppercase"
                style={{ fontSize: "10px", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => { if (window.confirm("Mark all conversations as read?")) markAllRead(); }}
              >
                Mark all as read
              </span>
            )}
          </div>

          <div style={{ height: "calc(100vh - 290px)", minHeight: "320px", display: "flex", position: "relative" }}>

            {/* ── Folder sidebar ── */}
            <div className="folders-sidebar">
              <div className="folders-sidebar__header">Folders</div>

              {folders.map((folder) => {
                const counts   = folderCounts[folder.id] || { total: 0, unread: 0 };
                const isActive  = activeFolder === folder.id;
                const isEditing = editingFolderId === folder.id;

                return (
                  <div
                    key={folder.id}
                    className={`folder-item${isActive ? " folder-item--active" : ""}`}
                    onClick={() => { if (!isEditing) setActiveFolder(folder.id); }}
                  >
                    <span className="folder-item__icon">{getFolderIcon(folder.name)}</span>

                    {isEditing ? (
                      <>
                        <input
                          className="folder-edit-input"
                          autoFocus
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")  renameFolder(folder.id);
                            if (e.key === "Escape") { setEditingFolderId(null); setEditingFolderName(""); }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={30}
                        />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); renameFolder(folder.id); }}>
                          <CheckIcon style={{ fontSize: "13px", color: "#45A9F0" }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); setEditingFolderName(""); }}>
                          <CloseIcon style={{ fontSize: "13px", color: "#BABABA" }} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Tooltip title={folder.name}>
                          <span className="folder-item__name">{folder.name}</span>
                        </Tooltip>
                        {counts.unread > 0 && (
                          <span className="folder-badge folder-badge--unread">{counts.unread}</span>
                        )}
                        <span className="folder-badge folder-badge--total">{counts.total}</span>
                        {!folder.isSystem && (
                          <>
                            <IconButton
                              size="small"
                              className="folder-edit-btn"
                              onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditingFolderName(folder.name); }}
                            >
                              <CreateIcon style={{ fontSize: "13px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              className="folder-delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Delete "${folder.name}"? Conversations will be moved to Inbox.`))
                                  deleteFolder(folder.id);
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: "13px" }} />
                            </IconButton>
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {folders.filter((f) => !f.isSystem).length >= Constants.maxCustomFolders ? (
                <div className="folder-limit-msg">Max {Constants.maxCustomFolders} folders reached</div>
              ) : isAddingFolder ? (
                <div className="folder-add-row">
                  <input
                    className="folder-add-input"
                    autoFocus
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")  addFolder();
                      if (e.key === "Escape") { setIsAddingFolder(false); setNewFolderName(""); }
                    }}
                    placeholder="Folder name..."
                    maxLength={30}
                  />
                  <IconButton size="small" onClick={addFolder}>
                    <CheckIcon style={{ fontSize: "13px", color: "#45A9F0" }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setIsAddingFolder(false); setNewFolderName(""); }}>
                    <CloseIcon style={{ fontSize: "13px", color: "#BABABA" }} />
                  </IconButton>
                </div>
              ) : (
                <div className="folder-add-btn" onClick={() => setIsAddingFolder(true)}>
                  <AddIcon style={{ fontSize: "14px", marginRight: "4px" }} />
                  New Folder
                </div>
              )}
            </div>

            {/* ── Contact list + chat ── */}
            <div style={{ flex: 1, position: "relative", minWidth: 0, height: "100%" }}>
              <MainContainer responsive>
                <Sidebar position="left" scrollable={false} id="v2-sidebar">
                  <Search
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={setSearchTerm}
                    onClearClick={() => setSearchTerm("")}
                  />

                  {/* Tag filter */}
                  <div ref={tagFilterAnchorRef} className="v2-tag-filter">
                    <div
                      className="v2-tag-filter__trigger"
                      onClick={() => {
                        if (!tagFilterOpen) {
                          setTagFilterOpen(true);
                          searchTags(tagFilterSearch, setTagFilterResults);
                        } else {
                          setTagFilterOpen(false);
                        }
                      }}
                    >
                      <span className={`v2-tag-filter__label${activeTagFilter ? " v2-tag-filter__label--active" : ""}`}>
                        {activeTagFilter ? activeTagFilter.name : "Filter by tag"}
                      </span>
                      {activeTagFilter ? (
                        <button
                          className="v2-tag-filter__clear"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            setActiveTagFilter(null);
                            setTagFilterSearch("");
                            setTagFilterOpen(false);
                          }}
                        >
                          ×
                        </button>
                      ) : (
                        <span style={{ fontSize: "9px", color: "#BABABA", marginLeft: "4px" }}>▾</span>
                      )}
                    </div>
                    <Popper
                      open={tagFilterOpen}
                      anchorEl={tagFilterAnchorRef.current}
                      placement="bottom-start"
                      style={{ zIndex: 9999, width: tagFilterAnchorRef.current ? tagFilterAnchorRef.current.offsetWidth : 160 }}
                    >
                      <ClickAwayListener onClickAway={() => setTagFilterOpen(false)}>
                        <Paper elevation={3}>
                          <input
                            className="v2-tag-filter__search"
                            placeholder="Search tags..."
                            value={tagFilterSearch}
                            autoFocus
                            onChange={(e) => {
                              const q = e.target.value;
                              setTagFilterSearch(q);
                              debouncedTagFilterSearch(q);
                            }}
                          />
                          <div className="v2-tag-filter__list">
                            {tagFilterResults.length === 0 ? (
                              <div className="v2-tag-filter__empty">No tags found</div>
                            ) : (
                              tagFilterResults.map((tag) => (
                                <div
                                  key={tag.id}
                                  className="v2-tag-filter__item"
                                  onClick={() => {
                                    setActiveTagFilter({ id: tag.id, name: tag.name });
                                    setTagFilterOpen(false);
                                    setTagFilterSearch("");
                                  }}
                                >
                                  {tag.name}
                                </div>
                              ))
                            )}
                          </div>
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </div>

                  <ConversationList>
                    {contactsLoading
                      ? [1, 2, 3, 4].map((i) => (
                          <Conversation key={`sk-${i}`} active={false}>
                            <Conversation.Content>
                              <div className="v2-skeleton-content">
                                <div className="v2-skeleton-line v2-skeleton-name" />
                                <div className="v2-skeleton-line v2-skeleton-msg" />
                                <div className="v2-skeleton-line v2-skeleton-time" />
                              </div>
                            </Conversation.Content>
                          </Conversation>
                        ))
                      : filteredContacts.map((contact) => {
                          const isActive    = contact.number === activeNumber;
                          const isIncoming  = contact.last_message_type === "incoming";
                          const relTime     = contact.last_message_at
                            ? moment(parseServerDate(contact.last_message_at)).fromNow()
                            : "";
                          return (
                            <Conversation
                              key={contact.number}
                              active={isActive}
                              onClick={() => openConversation(contact)}
                            >
                              <Conversation.Content>
                                <div style={{ position: "relative" }}>
                                  {/* Name + time row */}
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={{ fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "18px", fontSize: "13px", color: "#272833" }}>
                                      {(() => {
                                        const fn = contact.fname || "";
                                        const ln = contact.lname  || "";
                                        const full = (fn + " " + ln).trim();
                                        return full || contact.name || contact.number;
                                      })()}
                                      {contact.unread_count > 0 && (
                                        <span className="v2-unread-badge">{contact.unread_count}</span>
                                      )}
                                    </span>
                                    <span style={{ fontSize: "11px", color: "#BABABA", whiteSpace: "nowrap", flexShrink: 0 }}>
                                      {relTime}
                                    </span>
                                  </div>
                                  {/* Phone number sub-line (when name is available) */}
                                  {(contact.fname || contact.lname || contact.name) && (
                                    <div style={{ fontSize: "11px", color: "#BABABA", marginTop: "1px" }}>
                                      {contact.number}
                                    </div>
                                  )}
                                  {/* Last message preview */}
                                  <div style={{ fontSize: "12px", color: isIncoming ? "#122643" : "#6B6C7E", fontWeight: isIncoming ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: "2px" }}>
                                    {contact.last_message || "..."}
                                  </div>
                                  {/* Move to folder button */}
                                  <IconButton
                                    size="small"
                                    className="convo-move-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConvMoveAnchor(e.currentTarget);
                                      setConvMoveTarget(contact.number);
                                      setConvMoveCurrentFld(contact.folder_id);
                                    }}
                                  >
                                    <MoreVertIcon style={{ fontSize: "14px" }} />
                                  </IconButton>
                                </div>
                              </Conversation.Content>
                            </Conversation>
                          );
                        })}
                  </ConversationList>
                </Sidebar>

                <ChatContainer id="v2-chatcontainer">
                  <ConversationHeader>
                    <ConversationHeader.Back
                      onClick={() => {
                        const chatEl = document.getElementById("v2-chatcontainer");
                        const sideEl = document.getElementById("v2-sidebar");
                        if (chatEl) chatEl.style.display = "none";
                        if (sideEl) {
                          sideEl.style.display      = "flex";
                          sideEl.style["flex-basis"] = "auto";
                          sideEl.style.width         = "100%";
                          sideEl.style["max-width"]  = "100%";
                        }
                      }}
                    />
                    <ConversationHeader.Content>
                      <div>
                        <div className="col-left">
                          <p className="user-name">
                            {selectedContact?.name || contactData?.fname || (activeNumber ? activeNumber : "...")}
                          </p>
                          <p className="reference-no">{activeNumber || "..."}</p>
                        </div>
                        <div className="col-right">
                          <Stack>
                            <div className="icon-btn">
                              <select
                                onChange={(e) => setVirtualNumberId(e.target.value)}
                                value={virtualNumberId || ""}
                              >
                                {longcodes.map((l) => (
                                  <option key={l.number} value={l.number}>{l.number}</option>
                                ))}
                              </select>
                              <IconButton
                                ref={anchorRef}
                                id="v2-composition-button"
                                aria-controls={open ? "v2-composition-menu" : undefined}
                                aria-expanded={open ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={handleToggle}
                              >
                                <MoreVertIcon />
                              </IconButton>
                              <Popper
                                open={open}
                                anchorEl={anchorRef.current}
                                role={undefined}
                                placement="bottom-start"
                                transition
                                style={{ zIndex: 9999 }}
                              >
                                {({ TransitionProps, placement }) => (
                                  <Grow
                                    {...TransitionProps}
                                    style={{ transformOrigin: placement === "bottom-start" ? "left top" : "left bottom" }}
                                  >
                                    <Paper className="option">
                                      {selectedContact && (
                                        <ClickAwayListener onClickAway={handleClose}>
                                          <MenuList
                                            autoFocusItem={open}
                                            id="v2-composition-menu"
                                            aria-labelledby="v2-composition-button"
                                            onKeyDown={handleListKeyDown}
                                          >
                                            <MenuItem onClick={handleClose}>
                                              <p>
                                                {contactData?.id && (
                                                  <a
                                                    href={Constants.getAcContactUrl(contactData.id)}
                                                    style={{ textDecoration: "underline" }}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    View Contact
                                                  </a>
                                                )}
                                              </p>
                                            </MenuItem>
                                            <MenuItem onClick={handleClose}>
                                              <p>
                                                {contactData?.id && (
                                                  <a
                                                    href={`/u/${store.get("c_id")}/${contactData.id}`}
                                                    style={{ textDecoration: "underline" }}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    Optout
                                                  </a>
                                                )}
                                              </p>
                                            </MenuItem>
                                            <MenuItem
                                              onClick={() => {
                                                setOpen(false);
                                                if (selectedContact) {
                                                  setConvMoveTarget(selectedContact.number);
                                                  setConvMoveCurrentFld(selectedContact.folder_id);
                                                  setConvMoveAnchor(anchorRef.current);
                                                }
                                              }}
                                            >
                                              Move to folder
                                            </MenuItem>
                                            <MenuItem
                                              onClick={() => {
                                                setOpen(false);
                                                if (selectedContact) markUnread(selectedContact.number);
                                              }}
                                            >
                                              Mark as unread
                                            </MenuItem>
                                          </MenuList>
                                        </ClickAwayListener>
                                      )}
                                    </Paper>
                                  </Grow>
                                )}
                              </Popper>
                            </div>
                          </Stack>
                        </div>
                      </div>
                    </ConversationHeader.Content>
                  </ConversationHeader>

                  <MessageList>
                    {!activeNumber ? (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "#BABABA", fontSize: "14px", fontFamily: "Open Sans" }}>
                        Please select a contact to start conversation.
                      </div>
                    ) : threadLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px 0" }}>
                        <CircularProgress size={32} style={{ color: "#45A9F0" }} />
                      </div>
                    ) : (
                      thread.map((msg) => {
                        const isIncoming = msg.type === "incoming";
                        const senderName = isIncoming
                          ? (selectedContact?.name || contactData?.fname || activeNumber)
                          : (msg.sender_name || msg.virtual_number || "Automation");
                        return (
                          <Message
                            key={msg.id}
                            model={{ message: msg.message, direction: msg.type, position: "single" }}
                            style={msg.isOptimistic ? { opacity: 0.5 } : {}}
                          >
                            <Message.Header sender={senderName} />
                            <Message.Footer
                              sentTime={
                                msg.isOptimistic
                                  ? "Sending..."
                                  : parseServerDate(msg.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true })
                              }
                            />
                          </Message>
                        );
                      })
                    )}
                  </MessageList>

                  <MessageInput
                    placeholder={
                      !activeNumber         ? "Select a conversation..." :
                      contactLoading        ? "Loading contact..." :
                      contactNotFound       ? "Contact not found in ActiveCampaign" :
                                             "Write a message"
                    }
                    value={msgInputValue}
                    onChange={(val) => setMsgInputValue(val)}
                    onSend={sendReply}
                    disabled={!activeNumber || contactLoading || contactNotFound}
                    attachButton={false}
                  />
                </ChatContainer>

                {/* ── Tags right sidebar ── */}
                {activeNumber && (
                  <Sidebar position="right" scrollable className="v2-tags-sidebar">
                    <div className="v2-tags-sidebar__inner">

                      <div className="v2-tags-sidebar__section-label">Tags</div>

                      {/* Applied tags */}
                      <div className="v2-tags-sidebar__chips">
                        {contactTags.length === 0 ? (
                          <div className="v2-tags-sidebar__empty">No tags applied</div>
                        ) : (
                          contactTags.map((tag) => (
                            <span key={tag.id} className="v2-tag-chip">
                              <span className="v2-tag-chip__label">{tag.tag_name}</span>
                              <button
                                className="v2-tag-chip__remove"
                                onClick={() => removeContactTag(tag.id)}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        )}
                      </div>

                      <div className="v2-tags-sidebar__section-label v2-tags-sidebar__section-label--mt">
                        Add tag
                      </div>
                      <input
                        className="v2-tags-sidebar__search"
                        placeholder="Search tags..."
                        value={tagSearch}
                        onChange={(e) => {
                          const q = e.target.value;
                          setTagSearch(q);
                          debouncedTagSearch(q);
                        }}
                        onFocus={() => {
                          if (!tagSearchResults.length) searchTags("", setTagSearchResults);
                        }}
                      />

                      {/* Suggestions */}
                      {tagSearchResults
                        .filter((t) => !contactTags.some((ct) => ct.tag_id === t.id))
                        .map((tag) => {
                          const isAdding = addingTagId === tag.id;
                          return (
                            <div
                              key={tag.id}
                              className={`v2-tags-sidebar__result${isAdding ? " v2-tags-sidebar__result--adding" : ""}${addingTagId && !isAdding ? " v2-tags-sidebar__result--muted" : ""}`}
                              onClick={() => !addingTagId && addContactTag(tag)}
                            >
                              {isAdding && (
                                <CircularProgress size={11} style={{ color: "#45A9F0", marginRight: "6px", flexShrink: 0 }} />
                              )}
                              {tag.name}
                            </div>
                          );
                        })
                      }

                    </div>
                  </Sidebar>
                )}
              </MainContainer>
            </div>
          </div>
        </Paper>
      </div>

      {/* ── Move to folder menu ── */}
      <Popper
        open={Boolean(convMoveAnchor)}
        anchorEl={convMoveAnchor}
        placement="bottom-end"
        transition
        style={{ zIndex: 9999 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom-end" ? "right top" : "right bottom" }}
          >
            <Paper className="option" elevation={3}>
              <ClickAwayListener
                onClickAway={() => {
                  setConvMoveAnchor(null);
                  setConvMoveTarget(null);
                  setConvMoveCurrentFld(null);
                }}
              >
                <MenuList>
                  {convMoveTargetContact && (
                    <MenuItem
                      onClick={() => {
                        toggleReadStatus(convMoveTarget, convMoveTargetContact.unread_count > 0);
                        setConvMoveAnchor(null);
                        setConvMoveTarget(null);
                        setConvMoveCurrentFld(null);
                      }}
                      style={{ fontSize: "13px" }}
                    >
                      {convMoveTargetContact.unread_count > 0 ? "Mark as read" : "Mark as unread"}
                    </MenuItem>
                  )}
                  <Divider />
                  <MenuItem disabled style={{ fontSize: "12px", opacity: 0.6, minHeight: "unset" }}>
                    Move to folder
                  </MenuItem>
                  {folders.map((folder) => {
                    const isCurrent = folder.id === convMoveCurrentFld;
                    return (
                      <MenuItem
                        key={folder.id}
                        onClick={() => {
                          moveConversationToFolder(convMoveTarget, folder.id);
                        }}
                        selected={isCurrent}
                        style={{ fontSize: "13px" }}
                      >
                        <span style={{ flex: 1 }}>{folder.name}</span>
                        {isCurrent && (
                          <CheckIcon style={{ fontSize: "13px", marginLeft: "8px", color: "#45A9F0" }} />
                        )}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}
